import { ReactNode, useEffect, useMemo, useState } from "react";
import { useBlockLayout, useRowSelect, useTable } from "react-table";
import { useSticky } from "react-table-sticky";
import { getContractApply } from "src/api/contract/contract-api";
import { ContractManageList, ContractSearchParams, searchStatusOption } from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { PageMeta } from "src/api/public-types";
import { BaseCheckbox, BaseInput, BasePagination, BaseSelect } from "src/components";
import { BaseAbstractModal } from "src/components/BaseAbstractModal";
import { MultiSelectFilter } from "src/components/multiSelect";
import TableRadioButton from "src/pages/product/components/TableRadioButton";
import { ProductTypeLabels, ProductTypes } from "src/pages/product/product-types";

type Props = {
  pageMeta?: PageMeta;
  columns: any;
  title?: string;
  addedList?: any[];
  addableContractIds?: string[]; // 선택 가능한 계약 ID들
  onClick: (list: any[]) => void;
  onClose: () => void;
  children?: ReactNode;
  checkboxType?: "checkbox" | "radio";
  isSearched?: boolean;
};

const ContractModal = (props: Props) => {
  // 체크박스된 데이터

  const [selectedItems, setSelectedItems] = useState<Array<any>>([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [contracts, setContracts] = useState<Array<ContractManageList>>([]);
  const [pageMeta, setPageMeta] = useState<PageMeta>();
  const [currentSize, setCurrentSize] = useState(20);
  const [searchParams, setSearchParams] = useState<ContractSearchParams>({
    page: 0,
    size: 20,
    sort: {
      orders: [{ property: "contractManageId", direction: "ASC" }],
    },
    // spaceProductType: "TIME_COURT, FULL_COURT, NON_RESIDENT_OFFICE",
  });
  const { executeAsync: getContractList } = useApiOperation(getContractApply);
  const newList = useMemo(() => {
    if (props.checkboxType === "checkbox") {
      return contracts.map((item: any) => {
        const selects = selectedItems.map((select: any) => Number(select.contractManageId));
        return {
          checked: selects.includes(Number(item.contractManageId)),
          ...item,
        };
      });
    } else {
      return contracts;
    }
  }, [props.checkboxType, contracts, selectedItems]);

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows } = useTable(
    {
      columns: props.columns,
      data: newList,
      addedList: props.addedList || [],
    },
    useBlockLayout,
    useSticky,
    useRowSelect,
    (hooks) => {
      props.checkboxType &&
        hooks.visibleColumns.push((columns: Array<any>) => [
          {
            width: 50,
            Header: "선택",
            accessor: "checked",
            Cell: ({ row, toggleAllRowsSelected, toggleRowSelected }: any) => {
              let currentState = row.getToggleRowSelectedProps();
              return (
                <>
                  {props.checkboxType === "checkbox" ? (
                    <BaseCheckbox
                      id={row.id}
                      name={row.id}
                      checked={row.original.checked}
                      onChange={(checked: boolean) => {
                        if (checked) {
                          setSelectedItems((prev) => [...prev, row.original]);
                        } else {
                          setSelectedItems((prev) => prev.filter((item) => item.contractManageId !== row.original.contractManageId));
                        }
                      }}
                      disabled={isCheckDisabled(row.original)}
                    />
                  ) : (
                    <TableRadioButton
                      id={row.id}
                      disabled={isCheckDisabled(row.original)}
                      onClick={() => {
                        toggleAllRowsSelected(false);
                        toggleRowSelected(row.id, true);
                        setSelectedItems([row.original]);
                      }}
                      name="selectedProvider"
                      {...currentState}
                    />
                  )}
                </>
              );
            },
          },
          ...columns,
        ]);
    },
  );

  useEffect(() => {
    const fetchContractList = async () => {
      const { data } = await getContractList({
        ...searchParams,
      });
      setContracts(data.data.content);
      setPageMeta(data.meta.pageMeta);
    };
    fetchContractList();
  }, [searchParams]);

  useEffect(() => {
    props.isSearched === true && setSelectedItems([]);
  }, [props.isSearched]);

  const isCheckDisabled = (item: any) => {
    // addableContractIds가 있는 경우: 선택 가능한 ID 목록에 없으면 disabled
    if (props.addableContractIds) {
      const contractId = String(item.contractId);

      const isDisabled = !props.addableContractIds.find((value) => String(value) === String(contractId));

      return isDisabled;
    }
  };

  const tableWidth = useMemo(() => {
    let totalWidth = 0;
    headerGroups.forEach((headerGroup) => {
      headerGroup.headers.forEach((header) => (totalWidth += Number(header?.width || 0)));
    });
    return totalWidth;
  }, [headerGroups]);

  const returnContractSteps = useMemo(() => {
    let rtn: any = [];

    if (searchParams.contractStep !== undefined && searchParams.contractStep !== "") {
      rtn = searchParams.contractStep.split(",");
    }

    return rtn;
  }, [searchParams.contractStep]);

  return (
    <BaseAbstractModal size="xlarge" isOpen={true}>
      <section className="base-abstract-modal__title flex-center-between ">
        <h1>{props.title ? props.title : "항목 선택"}</h1>
        <div className="right-area">
          <div className="flex-center">
            <div className="minmax120">
              <MultiSelectFilter
                className="w-100"
                placeholder="상태"
                options={searchStatusOption}
                value={returnContractSteps || []}
                onChange={(values) => setSearchParams({ ...searchParams, contractStep: values.join(",") })}
              />
            </div>
            <div className="minmax120 mx10">
              <BaseSelect
                value={searchParams.spaceProductType}
                stateOptions={[
                  { value: "", label: "전체" },
                  { value: ProductTypes["FULL_COURT"], label: ProductTypeLabels["FULL_COURT"] },
                  { value: ProductTypes["TIME_COURT"], label: ProductTypeLabels["TIME_COURT"] },
                  { value: ProductTypes["NON_RESIDENT_OFFICE"], label: ProductTypeLabels["NON_RESIDENT_OFFICE"] },
                ]}
                setStateValue={(value: string) => {
                  setSearchParams({ ...searchParams, spaceProductType: value });
                }}
                placeholder="상품 유형"
              />
            </div>
            <BaseInput
              className="minmax240"
              type="text"
              value={searchKeyword}
              onChange={setSearchKeyword}
              onKeyUp={() => {
                setSearchParams({ ...searchParams, searchValue: searchKeyword });
              }}
              placeholder="검색어를 입력해주세요"
              onSearchClick={() => {}}
            />
          </div>
        </div>
      </section>
      <section className="base-abstract-modal__contents">
        <div {...getTableProps()} className="base-table view-data-table sticky px30">
          <div className="header">
            {headerGroups.map((headerGroup) => (
              <div {...headerGroup.getHeaderGroupProps()} className="base-table__tr">
                {headerGroup.headers.map((header) => {
                  return (
                    <div
                      {...header.getHeaderProps()} //
                      className={`base-table__th `}
                    >
                      {header.render("Header")}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          <div {...getTableBodyProps()} className="body">
            {rows.map((row: any) => {
              prepareRow(row);
              return (
                <div {...row.getRowProps()} className={`base-table__tr ${row.values.floor ? "bg-gray100" : ""}`}>
                  {row.cells.map((cell: any) => {
                    return (
                      <div {...cell.getCellProps()} className="base-table__td">
                        {cell.render("Cell")}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            <div>
              {rows.length === 0 && (
                <div className="base-table__tr table-cursor-unset" style={{ width: tableWidth }}>
                  <div className="base-table__td w-100 text-center">
                    <div className="w-100">{<span>데이터가 없습니다.</span>}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      {pageMeta ? (
        <section className="px30">
          <BasePagination
            pageIndex={pageMeta.pageRequest.page || 0}
            totalPages={pageMeta?.totalPages || 0}
            currentSize={currentSize}
            sizeOption={setCurrentSize}
            totalElements={pageMeta?.totalElements || 0}
            goPage={(page: number) => {
              setSearchParams({ ...searchParams, page });
            }}
            children={
              <div className="flex-center">
                <button
                  className="base-btn color-white mr10"
                  onClick={() => {
                    props.onClose && props.onClose();
                  }}
                >
                  취소
                </button>
                <button
                  disabled={selectedItems.length > 0 ? false : true}
                  className="base-btn"
                  onClick={() => {
                    props.onClick(selectedItems);
                    props.onClose();
                  }}
                >
                  선택
                </button>
              </div>
            }
          />
        </section>
      ) : (
        <section className="pa30  flex-center-end w-100">
          <div className="flex-center">
            <button
              className="base-btn minmax80 color-white mr10"
              onClick={() => {
                props.onClose && props.onClose();
              }}
            >
              취소
            </button>
            <button
              disabled={selectedItems.length > 0 ? false : true}
              className="base-btn minmax80 "
              onClick={() => {
                props.onClick(selectedItems);
                props.onClose();
              }}
            >
              선택
            </button>
          </div>
        </section>
      )}
    </BaseAbstractModal>
  );
};

export default ContractModal;
